import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../../config/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { VotingClosed } from '../VotingClosed';
import './styles.css';

// Hardcoded flag - set to true when voting is closed
const VOTING_IS_CLOSED = true;

const songs = [
  "WHERE HAVE I BEEN? - MONOLOGUE",
  "MYSTERY SONG COVER",
  "H3@RT$ W3RE M3@NT T0 F7Y",
  "SURVIVE",
  "CRUISIN",
  "WORST GUYS",
  "TALK MY SHIT",
  "GOT 2 BE",
  "IN THE NIGHT",
  "YOSHINOYA",
  "TO BE HUNTED",
  "URN (FULL VERSION)",
  "CRAWL",
  "WE ARE GOD",
  "FEELS LIKE SUMMER",
  "HUMAN SACRIFICE",
  "PLACE WHERE LOVE GOES",
  "NO EXCUSES",
  "ME AND UR MAMA",
  "DO YA LIKE",
  "SOBER",
  "LES",
  "HEARTBEAT",
  "SWEATPANTS",
  "THIS IS AMERICA",
  "BONFIRE",
  "3005",
  "REDBONE",
  "LITHONIA",
  "REAL LOVE",
  "DAMN RIGHT (JENNIE)",
  "WITCHY (KAYTRANADA)",
  "URN",
  "TELEGRAPH AVE",
  "SUMMERTIME MAGIC",
  "THE PALISADES",
  "IV. SWEATPANTS",
  "BLACK QUALLS (THUNDERCAT)",
  "RETRO (ROUGH)",
  "I. PINK TOES",
  "TERRIFIED",
  "FAVORITE SONG (CHANCE)",
  "BABY BOY",
  "WHAT KIND OF LOVE",
  "DADVOCATE",
  "FREAKS AND GEEKS",
  "STEPS BEACH",
  "PSILOCYBAE",
  "RIOT",
  "LITTLE FOOT BIG FOOT",
  "ATAVISTA",
  "SO FLY"
];

const Setlist = () => {
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Load user's saved selections when component mounts
  useEffect(() => {
    const loadUserSelections = async () => {
      if (!user) return;
      
      try {
        const userDocRef = doc(db, 'userSelections', user.uid);
        const docSnap = await getDoc(userDocRef);
        
        if (docSnap.exists()) {
          const savedSongs = docSnap.data().songs || [];
          setSelectedSongs(savedSongs);
          
          // Lock the setlist and show success ONLY if they have exactly 20 songs
          if (savedSongs.length === 20) {
            setIsLocked(true);
            setShowSuccess(true);
          } else {
            // Ensure users with < 20 songs can still edit
            setIsLocked(false);
            setShowSuccess(false);
          }
        }
      } catch (err) {
        console.error('Error loading selections:', err);
        setError('Failed to load your saved selections. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserSelections();
  }, [user]);

  const handleCheckboxChange = (song) => {
    setSelectedSongs(prev => {
      if (prev.includes(song)) {
        // If song is already selected, remove it
        return prev.filter(s => s !== song);
      } else if (prev.length < 20) {
        // Only add if we haven't reached the limit of 20
        return [...prev, song];
      } else {
        // Show error or message when limit is reached
        setError('You can only select up to 20 songs.');
        return prev;
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      const userDocRef = doc(db, 'userSelections', user.uid);
      await setDoc(userDocRef, {
        songs: selectedSongs,
        updatedAt: new Date().toISOString(),
        email: user.email
      });
      
      // Show success view and lock if they've saved 20 songs
      if (selectedSongs.length === 20) {
        setIsLocked(true);
      }
      setShowSuccess(true);
      
      // Auto-hide success message after 5 seconds if not locked
      if (selectedSongs.length < 20) {
        setTimeout(() => setShowSuccess(false), 5000);
      }
    } catch (err) {
      console.error('Error saving selections:', err);
      setError('Failed to save your selections. Please try again.');
    }
  };

  // Protect the route - redirect if not authenticated
  if (!user) {
    navigate('/');
    return null;
  }

  // Show voting closed message when voting is closed
  if (VOTING_IS_CLOSED) {
    return <VotingClosed />;
  }

  const rainbowClasses = [
    'rainbow-red',
    'rainbow-orange',
    'rainbow-yellow',
    'rainbow-green',
    'rainbow-blue'
  ];

  return (
    <div className="setlist-container">
      {error && <div className="error-message">{error}</div>}
      
      {isLoading ? (
        <div className="loading-message">Loading your selections...</div>
      ) : showSuccess && isLocked ? (
        <div className="success-view">
          <div className="success-header">
            <h2>✓ Your Setlist is Complete!</h2>
            <p className="success-subtitle">You've selected your 20 songs. See you at Camp Flog Gnaw!</p>
          </div>
          
          <div className="saved-setlist">
            <div className="saved-songs-list">
              {selectedSongs.map((song, index) => {
                const colorClass = rainbowClasses[index % 5];
                return (
                  <div key={`${song}-${index}`} className={`saved-song-item ${colorClass}`}>
                    <span className="saved-song-number">{String(index + 1).padStart(2, '0')}</span>
                    <span className="saved-song-title">{song}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <>
          {showSuccess && !isLocked && (
            <div className="success-message">
              ✓ Your selections have been saved! ({selectedSongs.length}/20)
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="setlist-form">
            <div className="songs-grid-container">
              <div className="songs-grid">
                {songs.map((song, index) => {
                  const colorClass = rainbowClasses[index % 5];
                  const isSelected = selectedSongs.includes(song);
                  
                  return (
                    <label key={song} className="song-item">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        className="visually-hidden"
                        onChange={() => handleCheckboxChange(song)}
                        disabled={isLocked}
                      />
                      <span className={`song-name ${isSelected ? colorClass : ''}`}>
                        {song}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            
            <button 
              type="submit" 
              className="submit-button"
              disabled={selectedSongs.length === 0 || isLocked}
            >
              Save My Setlist! ({selectedSongs.length}/20)
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default Setlist;
